import {
  Button,
  DateInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  getResolver,
  Label,
  RadioGroup,
  RadioGroupItem,
  toast,
} from '@folio/components';
import { formatPrice } from '@folio/lib';
import dayjs from 'dayjs';
import IsSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';
import {
  type Claim,
  type ClaimDto,
  keyValueFieldsToObject,
  useClaimsControllerDelete,
  useClaimsControllerUpdate,
} from 'korint-sdk';
import type { DateField, Field } from 'korint-sdk/react';
import { Trash } from 'lucide-react';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { StatusPill } from '@folio/components/ui/status-pill';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

dayjs.extend(IsSameOrBeforePlugin);

export function AssetClaimItem({
  claim,
  assetName,
  fieldsConfig,
  hasResponsibility,
  isHistoricalClaim,
  isReadonly,
  toDate,
  refetchClaims,
}: {
  claim: Claim;
  assetName?: string;
  fieldsConfig?: Field[];
  hasResponsibility?: boolean;
  isHistoricalClaim?: boolean;
  isReadonly?: boolean;
  toDate?: Date;
  refetchClaims: () => Promise<{ data: ClaimDto[] }>;
}) {
  const { t } = useTranslation();
  const itemId = useId();

  const { mutateAsync: updateClaim } = useClaimsControllerUpdate({
    mutation: {
      onError: () => {
        toast({
          description: t('assetClaims.updateError'),
        });
      },
    },
  });

  const { mutate: deleteClaim, isPending: isDeleting } = useClaimsControllerDelete({
    mutation: {
      onSuccess: () => refetchClaims(),
      onError: () => {
        toast({
          description: t('assetClaims.deleteError'),
        });
      },
    },
  });

  const occurredAtField: DateField = {
    name: 'occurredAt',
    type: 'date',
    required: false,
    max: toDate ? dayjs(toDate).format('YYYY-MM-DD') : undefined,
  };

  const form = useForm({
    values: { occurredAt: claim.occurredAt ? dayjs(claim.occurredAt).format('YYYY-MM-DD') : '' },
    resolver: getResolver([occurredAtField]),
  });

  const customFields = keyValueFieldsToObject(claim.customFields ?? []);

  return (
    <Stack gap={6} className="p-6">
      <Group align="stretch" justify="between" gap={0} className="text-md font-medium">
        <Group gap={2}>
          {assetName && <div className="text-sm font-medium">{assetName}</div>}
          {isHistoricalClaim && (
            <StatusPill color="bg-slate-200 text-amber-600" label={t('assetClaims.historical')} />
          )}
        </Group>
        {!isReadonly && (
          <Button
            type="button"
            variant="destructive-ghost"
            size="xs"
            disabled={isDeleting}
            loading={isDeleting}
            onClick={() => deleteClaim(claim)}
            icon={<Trash className="h-3" />}
          >
            {t('assetClaims.delete')}
          </Button>
        )}
      </Group>
      <div>
        {claim.reserve !== undefined && (
          <Label className="mb-4">
            {t('assetClaims.reserve', { amount: formatPrice(claim.reserve) })}
          </Label>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {hasResponsibility ? (
          <div>
            <Label className="mb-4">{t('assetClaims.responsibility')}</Label>
            <RadioGroup
              className="pt-3"
              defaultValue={claim.responsibility}
              disabled={isReadonly}
              onValueChange={(e: string) =>
                updateClaim({ id: claim.id, data: { responsibility: e } })
              }
            >
              <Group gap={8}>
                <RadioGroupItem value="NONE" id={`${itemId}NONE`} />
                <Label htmlFor={`${itemId}NONE`}>{t('claimResponsibility.NONE')}</Label>
              </Group>
              <Group gap={8}>
                <RadioGroupItem value="HALF" id={`${itemId}HALF`} />
                <Label htmlFor={`${itemId}HALF`}>{t('claimResponsibility.HALF')}</Label>
              </Group>
              <Group gap={8}>
                <RadioGroupItem value="FULL" id={`${itemId}FULL`} />
                <Label htmlFor={`${itemId}FULL`}>{t('claimResponsibility.FULL')}</Label>
              </Group>
            </RadioGroup>
          </div>
        ) : null}
        <div>
          <Form {...form}>
            <FormField
              name="occurredAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('assetClaims.occurredAt')}</FormLabel>
                  <FormControl>
                    <DateInput
                      value={field.value}
                      disabled={isReadonly}
                      onChange={date => {
                        form.setValue(field.name, date ?? '');

                        if (date) {
                          form.handleSubmit(async ({ occurredAt }) => {
                            await updateClaim({
                              id: claim.id,
                              data: {
                                occurredAt: dayjs(occurredAt).utc().toISOString(),
                              },
                            });
                          })();
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Form>
        </div>
      </div>
      {fieldsConfig?.length ? (
        <div className="grid grid-cols-2 gap-4">
          {fieldsConfig.map(field => {
            const value = customFields[field.name];
            return (
              <div key={field.name}>
                <Label className="text-sm">
                  {t(
                    [`fields.${field.name}.informationField.label`, `fields.${field.name}.label`],
                    { defaultValue: field.name },
                  )}
                </Label>
                <div className="text-sm">{value}</div>
              </div>
            );
          })}
        </div>
      ) : null}
    </Stack>
  );
}
